import React, { FC, useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Polygon, Tooltip, useMap } from "react-leaflet";
import { RED, GREEN, OPACITY, trimText } from "../RNMapView";
import { TooltipName } from "../../../../components/shared/TooltipName/TooltipName";
import { positionShapeTooltip } from "../utils/shapeTooltipAlignment";
import { ContrastName } from "../../../../components/shared/ContrastName/ContrastName";
import { getMapTooltipSizeRatio } from "../utils/getMapTooltipSizeRatio";

interface Props {
  id: string;
  isBooked: boolean;
  name: string;
  polygonal: {
    latitude: number;
    longitude: number;
    labelAlignment?: string;
  }[];
  eventHandlers?: L.LeafletEventHandlerFnMap;
  showTooltips?: boolean;
  showRoomMarkers?: boolean;
  scaleFactor?: number;
  mapReady?: boolean;
  hasContrastTooltips?: boolean;
}

const RNPolygonal: FC<Props> = ({
  id,
  isBooked,
  name,
  eventHandlers,
  polygonal,
  showRoomMarkers,
  showTooltips,
  scaleFactor,
  mapReady = false,
  hasContrastTooltips = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showRoomMarker, setShowRoomMarker] = useState(false);
  const polygonRef = useRef<L.Polygon | null>(null);
  const map = useMap();
  const labelAlignment = polygonal[0]?.labelAlignment || "center";
  const tooltipSizeRatio = getMapTooltipSizeRatio(scaleFactor);

  useEffect(() => {
    if (showTooltips === undefined || showRoomMarkers === undefined) {
      return;
    }

    setShowTooltip(showTooltips);
    setShowRoomMarker(showRoomMarkers);
  }, [showTooltips, showRoomMarkers]);

  // Position tooltip after map is ready, similar to BookingsFloorMap
  useEffect(() => {
    if (
      !polygonRef.current ||
      !map ||
      !showTooltip ||
      showRoomMarker ||
      !mapReady ||
      !["top", "bottom"].includes(labelAlignment)
    ) {
      return;
    }

    // Use requestAnimationFrame to ensure tooltip is in DOM
    const id = requestAnimationFrame(() => {
      positionShapeTooltip(polygonRef.current!, labelAlignment, map);
    });

    return () => cancelAnimationFrame(id);
  }, [labelAlignment, map, polygonal, showRoomMarker, showTooltip, mapReady]);

  return (
    <div>
      <Polygon
        ref={polygonRef}
        positions={polygonal.map((p) => [p.latitude, p.longitude])}
        fillOpacity={OPACITY}
        weight={2}
        pathOptions={{
          color: isBooked ? RED : GREEN,
          fillColor: isBooked ? RED : GREEN,
        }}
        eventHandlers={eventHandlers}
      >
        {showTooltip && !showRoomMarker && (
          <Tooltip
            className={`room-permanent-tooltip ${
              labelAlignment === "center-rotated" ? "rotated" : ""
            }`}
            direction="center"
            offset={[0, -2]}
            opacity={1}
            permanent
          >
            {hasContrastTooltips ? (
              <ContrastName
                name={trimText(name, 18)}
                scaleFactor={scaleFactor || 0.7}
                sizeRatio={tooltipSizeRatio}
                isClientApp
              />
            ) : (
              <TooltipName
                name={name}
                isBooked={isBooked}
                scaleFactor={scaleFactor}
                sizeRatio={tooltipSizeRatio}
              />
            )}
          </Tooltip>
        )}
      </Polygon>
    </div>
  );
};

export { RNPolygonal };
